import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlignCenter, AlignLeft, AlignRight, Bold, Italic,
} from 'lucide-react';
import FontSelector from '@/components/FontSelector';
import { ColorInput } from '@/components/ColorInput';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { SUBTITLE_STROKE_WIDTH_MAX } from '@/lib/subtitles/subtitleOutline';
import {
  FRAME_OUTLINE_WIDTH_PCT,
  type QuoteSlideTypography,
  type QuoteTextAlign,
} from '@/lib/quoteCarousel/quoteTextStyle';
import type { QuoteSlideBrand } from '@/lib/quoteCarousel/buildQuoteSlideProps';
import { QUOTE_SIZE_SCALE_MAX, QUOTE_SIZE_SCALE_MIN } from '@/lib/quoteCarousel/quoteTextLayout';
import type { QuoteTextStyle } from '@/lib/quoteCarousel/types';

interface QuoteTextStyleSectionProps {
  /** The carousel's overrides; undefined = the template's look. */
  textStyle: QuoteTextStyle | undefined;
  /** Effective look (template + overrides), what the controls display. */
  brand: QuoteSlideBrand;
  typography: QuoteSlideTypography;
  /** Template palette quick picks. */
  colorPresets?: string[];
  /** undefined = "Voltar ao template". */
  onChange: (textStyle: QuoteTextStyle | undefined) => void;
}

const ALIGN_OPTIONS: { value: QuoteTextAlign; icon: typeof AlignLeft }[] = [
  { value: 'left', icon: AlignLeft },
  { value: 'center', icon: AlignCenter },
  { value: 'right', icon: AlignRight },
];

function IconToggle({
  pressed, onClick, label, testId, children,
}: {
  pressed: boolean;
  onClick: () => void;
  label: string;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      aria-label={label}
      title={label}
      data-testid={testId}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-md border text-muted-foreground transition-colors',
        pressed ? 'border-primary bg-primary/10 text-foreground' : 'hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

/** "Texto" settings: carousel-wide font, colors, size, alignment, outline
 *  and weight/style, overriding the template. */
export const QuoteTextStyleSection: React.FC<QuoteTextStyleSectionProps> = ({
  textStyle, brand, typography, colorPresets, onChange,
}) => {
  const { t } = useTranslation();
  const patch = (next: Partial<QuoteTextStyle>) => onChange({ ...(textStyle ?? {}), ...next });
  const outline = textStyle?.outline ?? {
    enabled: !!typography.outline,
    color: typography.outline?.color ?? '#000000',
    widthPct: typography.outline?.widthPct ?? FRAME_OUTLINE_WIDTH_PCT,
  };
  // Sliders keep a local draft while dragging and write the carousel once,
  // on release (like the preview's zoom slider).
  const [sizeDraft, setSizeDraft] = useState<number | null>(null);
  const [outlineWidthDraft, setOutlineWidthDraft] = useState<number | null>(null);
  const sizePct = sizeDraft ?? Math.round(typography.sizeScale * 100);

  return (
    <div className="space-y-3 border-t pt-4" data-testid="quote-text-style">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{t('quoteCarousel.textStyle.title')}</Label>
        {textStyle && (
          <button
            type="button"
            data-testid="quote-text-style-reset"
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => onChange(undefined)}
          >
            {t('quoteCarousel.textStyle.reset')}
          </button>
        )}
      </div>

      <FontSelector
        value={brand.fontFamily}
        // Persist the file URL with the id (text-layer rule): the browser
        // renderer loads it directly.
        onValueChange={(fontId, font) => patch({ fontFamily: fontId, fontUrl: font?.file_url ?? null })}
        label={t('quoteCarousel.textStyle.font')}
        placeholder={brand.fontFamily}
        showPreview={false}
        className="space-y-1"
      />

      <div className="grid grid-cols-3 gap-2">
        {([
          ['textColor', 'text'],
          ['accentColor', 'accent'],
          ['backgroundColor', 'background'],
        ] as const).map(([field, labelKey]) => (
          <div key={field} className="space-y-1">
            <span className="block text-[11px] text-muted-foreground">{t(`quoteCarousel.textStyle.${labelKey}`)}</span>
            <ColorInput
              testId={`quote-text-style-${labelKey}`}
              value={brand[field]}
              presets={colorPresets}
              onChange={(color) => patch({ [field]: color })}
            />
          </div>
        ))}
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{t('quoteCarousel.textStyle.size')}</span>
          <span className="font-mono tabular-nums" data-testid="quote-text-style-size-value">{`${sizePct}%`}</span>
        </div>
        <Slider
          data-testid="quote-text-style-size"
          min={QUOTE_SIZE_SCALE_MIN * 100}
          max={QUOTE_SIZE_SCALE_MAX * 100}
          step={5}
          value={[sizePct]}
          onValueChange={([value]) => setSizeDraft(value)}
          onValueCommit={([value]) => {
            setSizeDraft(null);
            patch({ sizeScale: value / 100 });
          }}
          thumbProps={{ 'aria-label': t('quoteCarousel.textStyle.size') }}
        />
      </div>

      <div className="flex flex-wrap items-center gap-1">
        <div className="flex gap-1" role="group" aria-label={t('quoteCarousel.textStyle.align')}>
          {ALIGN_OPTIONS.map(({ value, icon: Icon }) => (
            <IconToggle
              key={value}
              pressed={typography.align === value}
              onClick={() => patch({ align: value })}
              label={t(`quoteCarousel.textStyle.align_${value}`)}
              testId={`quote-text-style-align-${value}`}
            >
              <Icon className="h-4 w-4" />
            </IconToggle>
          ))}
        </div>
        <span className="mx-1 h-5 w-px bg-border" aria-hidden="true" />
        <IconToggle
          pressed={typography.bold}
          onClick={() => patch({ bold: !typography.bold })}
          label={t('quoteCarousel.textStyle.bold')}
          testId="quote-text-style-bold"
        >
          <Bold className="h-4 w-4" />
        </IconToggle>
        <IconToggle
          pressed={typography.italic}
          onClick={() => patch({ italic: !typography.italic })}
          label={t('quoteCarousel.textStyle.italic')}
          testId="quote-text-style-italic"
        >
          <Italic className="h-4 w-4" />
        </IconToggle>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <Label htmlFor="quote-text-style-outline" className="text-[11px] font-normal text-muted-foreground">
            {t('quoteCarousel.textStyle.outline')}
          </Label>
          <Switch
            id="quote-text-style-outline"
            data-testid="quote-text-style-outline"
            checked={outline.enabled}
            onCheckedChange={(enabled) => patch({ outline: { ...outline, enabled } })}
          />
        </div>
        {outline.enabled && (
          <div className="flex items-center gap-2">
            <div className="w-24 shrink-0">
              <ColorInput
                testId="quote-text-style-outline-color"
                value={outline.color}
                presets={colorPresets}
                onChange={(color) => patch({ outline: { ...outline, color } })}
              />
            </div>
            <Slider
              data-testid="quote-text-style-outline-width"
              min={1}
              max={SUBTITLE_STROKE_WIDTH_MAX}
              step={1}
              value={[outlineWidthDraft ?? outline.widthPct]}
              onValueChange={([widthPct]) => setOutlineWidthDraft(widthPct)}
              onValueCommit={([widthPct]) => {
                setOutlineWidthDraft(null);
                patch({ outline: { ...outline, widthPct } });
              }}
              thumbProps={{ 'aria-label': t('quoteCarousel.textStyle.outlineWidth') }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteTextStyleSection;
